import React from "react";
import { useExcelJS } from "react-use-exceljs";
import { Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import i18n from "../../../../i18n";
import excelImages from "./images";
import { useTenderSearchStore } from "../../../../Stores/tenderSearch";
import { useAuthStore } from "../../../../Stores/authStore";

const data = [];
const letters = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "AA",
  "AB",
  "AC",
  "AD",
  "AE",
  "AF",
  "AG",
  "AH",
  "AI",
  "AJ",
  "AK",
  "AL",
  "AM",
  "AN",
  "AO",
  "AP",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AV",
  "AW",
  "AX",
  "AY",
  "AZ",
];
const cols = 10;
const normalLineHeight = 30;
const headingLineHeight = 100;
// const lotsTableStartPosition = 2;
const footerStartCol = 0;
const headerStartCol = 0;
// const paddingBetweenValsAndTable = 30;
const paddingToFooter = 60;

const addTitle = (worksheet, tenderTitle) => {
  const firstRow = worksheet.addRow();
  firstRow.height = 100;
  const secondRow = worksheet.addRow();
  worksheet.mergeCells(2, 2, 2, cols + 1);
  const headingCell = worksheet.getCell(`B2`);
  headingCell.value = tenderTitle;
  headingCell.font = {
    name: "Oswald",
    family: 2,
    size: 24,
    bold: true,
  };
  headingCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  autoHeightHeading(secondRow, headingLineHeight);
};

const getTranslatedValue = (translations, type) => {
  if (translations?.[i18n.language.slice(0, 2)]?.[type]) {
    return translations?.[i18n.language.slice(0, 2)]?.[type];
  } else if (translations?.it?.[type]) {
    return translations?.it?.[type];
  } else if (translations?.en?.[type]) {
    return translations?.en?.[type];
  } else if (translations?.[Object.keys(translations)[0]]?.[type]) {
    return translations?.[Object.keys(translations)[0]]?.[type];
  } else {
    return i18n.t("N/A");
  }
};

export default function TenderSearchXLSX({ tenders, admin }) {
  const { t } = useTranslation();
  const fileName = `${t("Tenders Club search export")} - ${dayjs().format("YYYY-MM-DD HH:mm:ss")}`;

  const [
    includeAdminColumns,
  ] = useTenderSearchStore((state) => [
    state.includeAdminColumns,
  ]);


  let excel = useExcelJS({
    filename: `${fileName}.xlsx`,
    intercept: (workbook) => {
      workbook.creator = "Tenders Club";
      workbook.created = new Date();
      workbook.modified = new Date();

      const worksheet = workbook.addWorksheet("Tenders", {
        pageSetup: { paperSize: 9, orientation: "landscape" },
        views: [{ showGridLines: false }],
      });
      addTitle(worksheet, fileName);
      worksheet.addRow(); //padding row

      // Base keys that should always be included regardless of data
      const essentialKeys = [
        "id",
        "translations", // Always include translations as they contain core tender info
      ];
      
      // All possible keys that we'll check for data
      const optionalKeys = [
        "notice_type",
        "products",
        "products_reranked",
        "llm_products",
        "currency",
        "estimated_total_value",
        "procedure_type",
        "award_criteria",
        "platform_id",
        "tender_national_id",
        "publication_date",
        "closing_date",
        "extension_date",
        "extension",
        "tender_url",
        "tendering_authority",
        "cpv",
        "nut_code",
        "nut_label",
        "soa_categories",
        "soa_category_labels",
      ];
      
      // Admin columns that we'll check if admin is true
      const adminKeys = [
        "allow_public_view",
        "confirmed",
        "confirmation_state",
        "original_tender",
        "confirmed_by",
        "confirmed_at",
        "scraped_at",
        "updated_at",
        "created_at",
        "original_language",
      ];
      
      // Function to check if a key exists in any tender and has data
      const keyHasData = (key, tenders) => {
        // First check if the key exists in any tender
        const keyExists = tenders.some(tender => key in tender);
        
        // If the key doesn't exist in any tender, we can skip further checks
        if (!keyExists) {
          return false;
        }
        
        // If the key exists, check if it has meaningful data in any tender
        return tenders.some(tender => {
          // Skip if this particular tender doesn't have the key
          if (!(key in tender)) {
            return false;
          }
          
          const value = tender[key];
          
          // Check for arrays (like products, cpv, etc.)
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          
          // Check for objects (like tendering_authority)
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            return Object.keys(value).length > 0;
          }
          
          // Special handling for strings - check if they contain non-whitespace characters
          if (typeof value === 'string') {
            return value.trim() !== '';
          }
          
          // Check for numbers and booleans
          if (typeof value === 'number' || typeof value === 'boolean') {
            return true;
          }
          
          // For all other cases, check if the value exists
          return value !== undefined && value !== null;
        });
      };
      
      // Filter optional keys to only include those that exist and have data
      const optionalKeysWithData = optionalKeys.filter(key => 
        keyHasData(key, tenders)
      );
      
      // Filter admin keys if they're included
      const adminKeysWithData = useAuthStore.getState()?.user?.is_superuser && includeAdminColumns 
        ? adminKeys.filter(key => keyHasData(key, tenders))
        : [];
      
      // Combine all keys
      let keys = [...essentialKeys, ...optionalKeysWithData, ...adminKeysWithData];

      const setColumnWidths = (worksheet, keys) => {
        // Define width mappings by key name based on typical content size
        const keyWidthMap = {
          // Narrow columns for IDs and short values
          id: 12,
          currency: 10,
          platform_id: 15,
          tender_national_id: 18,
          nut_code: 15,
          
          // Medium columns for dates and standard fields
          notice_type: 20,
          procedure_type: 20,
          award_criteria: 20,
          publication_date: 18,
          closing_date: 18,
          extension_date: 18,
          extension: 25,
          estimated_total_value: 18,
          confirmed: 12,
          confirmed_at: 18,
          scraped_at: 18,
          updated_at: 18,
          created_at: 18,
          original_language: 15,
          
          // Wider columns for text content
          tender_url: 40,
          
          // Extra wide for product lists and complex data
          products: 35,
          products_reranked: 35,
          llm_products: 35,
          cpv: 30,
          tendering_authority: 35,
          nut_label: 25,
          soa_categories: 25,
          soa_category_labels: 30,
          
          // Special case for translations (which spans 3 columns)
          translations_title: 40,
          translations_procedure: 25,
          translations_award: 25,
          
          // Default width for any other columns
          default: 20,
        };
        
        let colIndex = 1; // Excel columns are 1-indexed
        
        // Iterate through keys and set column widths
        keys.forEach(key => {
          if (key === "translations") {
            // Handle the special case of translations which spans 3 columns
            worksheet.getColumn(colIndex).width = keyWidthMap.translations_title;
            worksheet.getColumn(colIndex + 1).width = keyWidthMap.translations_procedure;
            worksheet.getColumn(colIndex + 2).width = keyWidthMap.translations_award;
            colIndex += 3;
          } else {
            // Set width based on key name or use default
            const width = keyWidthMap[key] || keyWidthMap.default;
            worksheet.getColumn(colIndex).width = width;
            colIndex++;
          }
        });
      };
      
      // Apply column widths
      setColumnWidths(worksheet, keys);

      let headerRow = worksheet.addRow(); //add headings for columns
      let pos = 0;
      for (let i = 0; i < keys.length; i++) {
        if (keys[pos] === "translations") {
          headerRow.getCell(letters[i]).value = t(`${t("TITLE")}`);
          headerRow.getCell(letters[i + 1]).value = t(`${t("PROCEDURE TYPE")}`);
          headerRow.getCell(letters[i + 2]).value = t(`${t("AWARD CRITERIA")}`);
          i += 2;
        } else {
          headerRow.getCell(letters[i]).value = t(`${keys[pos].toUpperCase().replaceAll("_", " ")}`);
        }
        pos++;
      }

      for (let i = 0; i < tenders.length; i++) {
        //add remaining content
        let newRow = worksheet.addRow();
        // let rowNumber = newRow._number;
        let secondPos = 0;

        for (let j = 0; j < keys.length; j++) {
          let key = keys[secondPos];
          
          // Get cell reference before the switch statement
          const cell = newRow.getCell(letters[j]);
          
          // Set alignment and text wrapping for all cells
          cell.alignment = {
            vertical: 'top',
            horizontal: 'left',
            wrapText: true
          };

          switch (key) {
            case "translations":
              cell.value = getTranslatedValue(tenders[i].translations, "title");
              newRow.getCell(letters[j + 1]).value = getTranslatedValue(
                tenders[i].translations,
                "procedure_type"
              );
              newRow.getCell(letters[j + 2]).value = getTranslatedValue(
                tenders[i].translations,
                "award_criteria"
              );
              
              // Also set alignment for the additional cells
              newRow.getCell(letters[j + 1]).alignment = {
                vertical: 'top',
                horizontal: 'left',
                wrapText: true
              };
              
              newRow.getCell(letters[j + 2]).alignment = {
                vertical: 'top',
                horizontal: 'left',
                wrapText: true
              };
              
              j += 2;
              break;
            case "products":
            case "products_reranked":
            case "llm_products":
              // Check if the key exists and contains array values
              if (tenders[i][key] && Array.isArray(tenders[i][key]) && tenders[i][key].length > 0) {
                // Join array elements with newlines
                cell.value = tenders[i][key].join("\n");
              } else {
                cell.value = "";
              }
              break;
            case "lot":
              let string = "";
              for (let lot of tenders[i].lot) {
                string += `${"ID"}: ${lot?.id},
                ${"Value"}: ${lot?.value},
                ${"Description"}: ${lot?.description},
                ${"Tender National ID"}: ${lot?.tender_national_id},
                ${"CPV ID"}: ${lot?.cpv_id},
                ${"CPV"}: ${lot?.cpv},
                ${"CPV Code"}: ${lot?.cpv_code}\n`;
              }
              cell.value = string;
              break;
            case "attachment":
              let attachmentString = "";
              for (let attachment of tenders[i].attachment) {
                attachmentString += `${"ID"}: ${attachment?.id},
                ${"Path"}: ${attachment?.path},
                ${"Paperless File ID"}: ${attachment?.paperless_file_id},
                ${"Description"}: ${attachment?.description},
                ${"Text"}: ${attachment?.text},
                ${"Tags"}: ${attachment?.tags ? attachment.tags.join(", ") : ""},
                ${"Tender ID"}: ${attachment?.tender}\n`;
              }
              cell.value = attachmentString;
              break;
            case "cpv":
              cell.value = tenders[i]?.cpv?.join(", ") || "";
              break;
            case "tendering_authority":
              if (tenders[i]?.tendering_authority?.nut_code) {
                let taString = `${"Name"}: ${tenders[i]?.tendering_authority?.name},
                ${"ID"}: ${tenders[i]?.tendering_authority?.id},
                ${"NUT ID"}: ${tenders[i]?.tendering_authority?.nut_id},
                ${"NUT Code"}: ${tenders[i]?.tendering_authority?.nut_code}\n`;

                cell.value = taString;
              } else {
                cell.value = tenders[i]?.tendering_authority;
              }
              break;
            default:
              let value = tenders[i][key];
              cell.value = value;
          }
          secondPos++;
        }
        newRow.font = { name: "Montserrat", family: 2, size: 10 };

        autoHeight(newRow, normalLineHeight);
      }

      const paddingRow = worksheet.addRow();
      paddingRow.height = paddingToFooter;
      const lastRow = worksheet.addRow();
      lastRow.alignment = {
        vertical: "middle",
        horizontal: "middle",
        wrapText: false,
      };
      const footerRow = lastRow._number - 1;

      addHeader(workbook, worksheet, cols);

      addFooter(workbook, worksheet, footerRow);
    },
    worksheets: [],
  });

  const exportXLSX = () => {
    excel.download(data);
  };

  return (
    <Button className="tc-full-width-small" onClick={() => exportXLSX()} type="button" color="secondary">
      {t("Download .xlsx")}
    </Button>
  );
}

export const addHeader = (workbook, worksheet, cols) =>{
  const topLeftShapes = workbook.addImage({
    base64: excelImages.summaryPDFShapes,
    extension: "png",
  });
  const topRightLogo = workbook.addImage({
    base64: excelImages.tendersClubLogoAndTagline,
    extension: "jpg",
  });

  worksheet.addImage(topLeftShapes, {
    tl: { col: headerStartCol, row: 0 },
    ext: { width: 170, height: 100 },
  });

  worksheet.addImage(topRightLogo, {
    tl: { col: cols - 2, row: 0 },
    ext: { width: 300, height: 95 },
  });
};

export const addFooter = (workbook, worksheet, footerRow) => {
  const bottomLeftAppaltiLogo = workbook.addImage({
    base64: excelImages.appaltiLogo,
    extension: "png",
  });

  worksheet.addImage(bottomLeftAppaltiLogo, {
    tl: { col: footerStartCol, row: footerRow },
    ext: { width: 60, height: 75 },
  });

  const footerCell1 = worksheet.getCell(`${letters[footerStartCol + 3]}${footerRow + 2}`);
  footerCell1.value = "Ufficio Appalti Srl";
  footerCell1.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: false,
  };
  const footerCell2 = worksheet.getCell(`${letters[footerStartCol + 6]}${footerRow + 2}`);
  footerCell2.value = "Milano. +39 02 92853681";
  footerCell2.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: false,
  };
  const footerCell3 = worksheet.getCell(`${letters[footerStartCol + 10]}${footerRow + 2}`);
  footerCell3.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: false,
  };
  footerCell3.value = {
    text: "info@tenders.club",
    hyperlink: "mailto:info@tenders.club",
    tooltip: "info@tenders.club",
  };
  const footerCell4 = worksheet.getCell(`${letters[footerStartCol + 10]}${footerRow + 3}`);
  footerCell4.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: false,
  };
  footerCell4.value = {
    text: "info@ufficioappalti.com",
    hyperlink: "mailto:info@ufficioappalti.com",
    tooltip: "info@ufficioappalti.com",
  };
};

const autoHeightHeading = (row, lineHeight) => {
  let maxLine = 1;
  row.eachCell((cell) => {
    maxLine = Math.max(cell.value.length / 60, maxLine);
  });

  row.height = lineHeight * maxLine;
};

const autoHeight = (row, lineHeight) => {
  let maxLine = 1;
  row.eachCell((cell) => {
    if (!cell.value) return;
    
    // Count actual newlines in the cell value
    const cellValue = cell.value.toString();
    const newlineCount = (cellValue.match(/\n/g) || []).length + 1;
    
    // Use the greater of newline count or character length estimate
    const charLengthEstimate = cellValue.length / 70;
    maxLine = Math.max(newlineCount, charLengthEstimate, maxLine);
  });
  
  if (lineHeight * maxLine > 200) {
    row.height = 200;
  } else {
    row.height = lineHeight * maxLine;
  }
};
